import { network, saveBlobAsFile } from '@utils'
import { handleReturn, MutationFunction, PaginatedResponse, QueryFunction } from './root'
import { InvoiceDeliveryMethod } from '@root/constants'

export enum ContactType {
  UNSET = 'UNSET',
  PERSON = 'PERSON',
  BUSINESS = 'BUSINESS'
}

export interface IContact {
  id: number
  type: ContactType
  customer_id?: string
  name: string
  name_aliases: string[]
  contact_business_id: string
  xrechnung_buyer_reference?: string | null
  notes: string
  phone_number?: string
  is_invoicing_enabled: boolean
  invoicing_email?: string
  invoicing_einvoice_address?: string
  invoicing_einvoice_operator?: string
  invoicing_tax_code?: string
  invoicing_street?: string
  invoicing_city?: string
  invoicing_postal_code?: string
  invoicing_country?: string
  invoicing_language?: string
  buyer_postal_address_details_xml_override?: string
  can_be_invoiced: boolean
  can_be_invoiced_via_email: boolean
  can_be_invoiced_via_einvoice: boolean
}

interface ContactListProps {
  slug: string
}

export const fetchContacts: QueryFunction<ContactListProps, PaginatedResponse<IContact>> = async (
  { slug },
  params = null
) => {
  const url = `/v1/business/${slug}/contacts/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const exportContactsCsv = async (
  { slug }: ContactListProps,
  params: Record<string, unknown> | null = null
): Promise<void> => {
  const url = `/v1/business/${slug}/contacts/export/csv/`
  const response = await network.httpClient.request({
    url,
    method: 'GET',
    params,
    responseType: 'blob'
  })

  const filename =
    response.headers?.['content-disposition']?.split('filename=')[1]?.slice(1, -1) ??
    `${slug}_contacts.csv`

  saveBlobAsFile(response.data, filename)
}

export const createContact: MutationFunction<ContactListProps, IContact> = async (
  { slug },
  requestData
) => {
  const url = `/v1/business/${slug}/contacts/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface ContactInstanceProps extends ContactListProps {
  contactId: number
}

export const fetchContact: QueryFunction<ContactInstanceProps, IContact> = async ({
  slug,
  contactId
}) => {
  const url = `/v1/business/${slug}/contacts/${contactId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface SuggestContactProps extends ContactListProps {
  query: string
}

export const suggestContact: QueryFunction<SuggestContactProps, IContact | undefined> = async ({
  slug,
  query
}) => {
  const url = `/v1/business/${slug}/contacts/suggest/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params: { query }
  })
  return data
}

export const updateContact: MutationFunction<ContactInstanceProps, IContact> = async (
  { slug, contactId },
  requestData
) => {
  const url = `/v1/business/${slug}/contacts/${contactId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteContact: MutationFunction<ContactInstanceProps, void> = async ({
  slug,
  contactId
}) => {
  const url = `/v1/business/${slug}/contacts/${contactId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'DELETE',
    validateStatus: () => true
  })

  return handleReturn({ status, data })
}

export interface ContactDeliveryMethod {
  name: string
  info?: string
  method: InvoiceDeliveryMethod
  errors: Array<string>
}

interface DeliveryMethodProps {
  slug: string
  contactId: number
}

export const fetchContactDeliveryMethods: QueryFunction<
  DeliveryMethodProps,
  Array<ContactDeliveryMethod>
> = async ({ slug, contactId }) => {
  const url = `/v1/business/${slug}/contacts/delivery_methods/${contactId}`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}
